import {useState} from 'react'
import useEffectEvent from './use-effect-event'
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect'

/**
 * Measures an element while the viewport moves under it, and reports each new
 * value.
 *
 * `measure(element)` runs in a layout effect, before paint, and again on window
 * `scroll` and `resize`. Listeners are passive and reads are coalesced into one
 * `requestAnimationFrame`. `onMeasured(value)` runs only when the value
 * changed; a `null` or `undefined` reading reports nothing.
 *
 * Those two events are the only triggers — there is no `ResizeObserver` — so a
 * change the window never hears about stays stale until the next scroll or
 * resize. Inline callbacks are safe, `useEffectEvent` keeps the listeners in
 * place — so a changed `measure` does not trigger a read either, the next one
 * waits for a scroll or resize. SSR-safe.
 *
 * @param {function(HTMLElement): *} measure - Reads the element and returns the
 *        measured value.
 * @param {function(*): void} onMeasured - Called with every changed value.
 * @param {Object} [options] - Optional configuration.
 * @param {boolean} [options.active=true] - Whether to measure.
 * @returns {Function} Ref callback for the element to measure.
 *
 * @example
 * const setElement = useViewportMeasurement(
 *   (element) => Math.max(0, element.getBoundingClientRect().top),
 *   (top) => console.log(top),
 *   {active: open}
 * )
 */
const useViewportMeasurement = (measure, onMeasured, {active = true} = {}) => {
    // State, not a ref: the effect must run again once the element arrives.
    const [element, setElement] = useState(null)

    // Inline callbacks must not restart the listeners.
    const onMeasure = useEffectEvent(measure)
    const onValue = useEffectEvent(onMeasured)

    useIsomorphicLayoutEffect(() => {
        if (!active || !element || typeof window === 'undefined') return undefined

        let frame = null
        let reported = null

        const read = () => {
            frame = null
            const value = onMeasure(element)
            if (value === null || value === undefined || value === reported) return
            reported = value
            onValue(value)
        }

        read()

        const schedule = () => {
            if (frame === null) {
                frame = window.requestAnimationFrame(read)
            }
        }

        window.addEventListener('scroll', schedule, {passive: true})
        window.addEventListener('resize', schedule, {passive: true})

        return () => {
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            if (frame !== null) {
                window.cancelAnimationFrame(frame)
            }
        }
    }, [active, element, onMeasure, onValue])

    return setElement
}

export default useViewportMeasurement
